import { useCallback } from 'react';

import { message as antMessage } from 'antd';
import { useTranslation } from 'react-i18next';

// action 回傳 result 的 shape：result.success / result.data.{ job_id, message }
interface ImportActionResult {
  success?: boolean;
  message?: string;
  data?: {
    job_id?: string;
    message?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

type AddJobFn = (jobId: string, jobName: string) => void;

interface UseImportI18nKeys {
  noFile?: string;
  invalidFile?: string;
  success?: string;
  error?: string;
  [key: string]: string | undefined;
}

interface UseImportConfig {
  action: (formData: FormData) => Promise<ImportActionResult>; // New: Service method (formData) => Promise
  addJob?: AddJobFn;
  jobName?: string;
  onSuccess?: (data: unknown) => void;
  typeParamName?: string; // 參數名稱，預設為 'import_type'，訂單匯入使用 'order_type'
  i18nKeys?: UseImportI18nKeys;
}

/**
 * 共用的匯入 Hook
 *
 * 功能：
 * - 處理匯入邏輯（檔案上傳、轉 Base64、呼叫 API、啟動背景任務）
 * - 支援 Admin / Operator 兩種角色
 * - 自動處理錯誤提示
 * - 支援多語系
 *
 * @param {string} typeParamName - 類型參數名稱，預設為 'import_type'，訂單匯入使用 'order_type'
 */
export function useImport({
  action, // New: Service method (formData) => Promise
  addJob,
  jobName = 'common.button.import',
  onSuccess,
  typeParamName = 'import_type' // 參數名稱，預設為 'import_type'，訂單匯入使用 'order_type'
}: UseImportConfig): (
  file: File,
  merchantId?: string | null,
  overrideExisting?: boolean,
  importType?: string | null,
  attachments?: File[]
) => Promise<string | null | undefined> {
  const { t, i18n } = useTranslation();

  // ... (log) ...

  // ... (defaultI18nKeys) ...

  // ... (keys) ...

  // ... (fileToBase64) ...

  const handleImport = useCallback(async (
    file: File,
    merchantId: string | null = null,
    overrideExisting: boolean = true,
    importType: string | null = null,
    attachments: File[] = []
  ): Promise<string | null | undefined> => {
    // ... (validation) ...

    try {
      // 顯示上傳中提示
      const hideLoading = antMessage.loading(t('shared.import.progress.uploading') as string, 0);

      const form = new FormData();

      form.append("locale", i18n.language);

      if (merchantId) {
        form.append("import[merchant_id]", merchantId);
      }

      form.append("import[override_existing]", overrideExisting ? "1" : "0");

      // Add type parameter if provided (e.g., import_type or order_type)
      if (importType) {
        form.append(`import[${typeParamName}]`, importType);
      }

      // Attach main import file
      form.append("import[import_file]", file);

      // Attach additional files if provided
      if (attachments && attachments.length > 0) {
        attachments.forEach((attachment) => {
          form.append("import[attachments][]", attachment);
        });
      }

      const result = await action(form);

      // 關閉上傳中提示
      hideLoading();

      if (result.success) {
        const { job_id, message: msg } = result.data as { job_id: string; message?: string };

        console.log('[useImport] Import success:', { job_id, jobName, hasAddJob: !!addJob });

        // 如果有背景任務管理，添加任務
        if (addJob && typeof addJob === 'function') {
          console.log('[useImport] Adding job to background manager:', job_id, jobName);
          addJob(job_id, jobName);
        } else {
          console.warn('[useImport] addJob function not available:', addJob);
        }

        // 呼叫成功回調
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(result.data);
        }

        // 返回 job_id 給 caller
        return job_id;
      }
      antMessage.error(result.message as string);
      return null;

    } catch (error) {
      console.error('Import error:', error);
      antMessage.error((error as Error).message);
    }
  }, [action, addJob, jobName, i18n.language, onSuccess, t, typeParamName]);

  return handleImport;
}

export default useImport;
